import api from '@/api/api.js'
import { DataLoadState } from '@/utils/storeUtils.js'

export const GAMECAM_STORE_NAMESPACE = 'gamecam'
export const GAMECAMS_SEARCH_STORE_NAMESPACE = 'gamecamsSearch'

// Mutation names
const SET_EMAIL = 'setEmail'
const SET_LOG_LOAD_STARTED = 'setLogLoadStarted'
const SET_LOG_LOAD_FINISHED_SUCCESSFULLY = 'setLogLoadFinishedSuccessfully'
const SET_LOG_LOAD_FAILED = 'setLogLoadFailed'
const RESET_LOG = 'resetLog'

const SET_HUNT_AREAS_LOAD_STARTED = 'setHuntAreasLoadStarted'
const SET_HUNT_AREAS_LOAD_FINISHED_SUCCESSFULLY = 'setHuntAreasLoadFinishedSuccessfully'
const SET_HUNT_AREAS_LOAD_FAILED = 'setHuntAreasLoadFailed'
const RESET_HUNT_AREAS = 'resetHuntAreas'

// Getter names
export const GET_EMAIL = 'getEmail'
export const IS_LOADING_LOG = 'isLoadingLog'
export const GET_LOG = 'getLog'
export const GET_LOG_LOAD_ERROR = 'getLogLoadError'
export const HAS_EMPTY_LOG_LOAD_RESULT = 'hasEmptyLogLoadResult'

export const IS_LOADING_HUNT_AREAS = 'isLoadingHuntAreas'
export const GET_HUNT_AREAS = 'getHuntAreas'
export const GET_HUNT_AREAS_LOAD_ERROR = 'getHuntAreasLoadError'
export const HAS_EMPTY_HUNT_AREAS_LOAD_RESULT = 'hasEmptyHuntAreasLoadResult'

// Action names
export const LOAD_LOG_FOR_EMAIL = 'loadLogForEmail'
export const RESET_LOG_STATE = 'resetLogState'
export const LOAD_HUNT_AREAS = 'loadHuntAreas'

// GPS devices store implementation
export const GamecamStore = {
  namespaced: true,

  state: () => ({ // state must be a function to allow reuse of this store
    email: null,
    logState: new DataLoadState(),
    huntAreasState: new DataLoadState()
  }),

  mutations: {
    [SET_EMAIL] (state, { email }) {
      state.email = email
    },

    [SET_LOG_LOAD_STARTED] (state, { email }) {
      const dataLoadState = new DataLoadState(email)
      state.logState = dataLoadState.stateForLoadStarted()
    },

    [SET_LOG_LOAD_FINISHED_SUCCESSFULLY] (state, { email, log }) {
      if (email !== state.logState.id) {
        // Log load is stale, ignore
        return
      }

      state.logState = state.logState.stateForLoadFinished(log)
    },

    [SET_LOG_LOAD_FAILED] (state, { email, error }) {
      if (email !== state.logState.id) {
        // Log load is stale, ignore
        return
      }

      state.logState = state.logState.stateForLoadFailed(error)
    },

    [RESET_LOG] (state) {
      state.logState = new DataLoadState()
    },

    [SET_HUNT_AREAS_LOAD_STARTED] (state, { email }) {
      const dataLoadState = new DataLoadState(email)
      state.huntAreasState = dataLoadState.stateForLoadStarted()
    },

    [SET_HUNT_AREAS_LOAD_FINISHED_SUCCESSFULLY] (state, { email, huntAreas }) {
      if (email !== state.huntAreasState.id) {
        // Hunt areas load is stale, ignore
        return
      }

      state.huntAreasState = state.huntAreasState.stateForLoadFinished(huntAreas)
    },

    [SET_HUNT_AREAS_LOAD_FAILED] (state, { email, error }) {
      if (email !== state.huntAreasState.id) {
        // Hunt areas load is stale, ignore
        return
      }

      state.huntAreasState = state.huntAreasState.stateForLoadFailed(error)
    },

    [RESET_HUNT_AREAS] (state) {
      state.huntAreasState = new DataLoadState()
    }
  },

  getters: {
    [GET_EMAIL]: state => {
      return state.email
    },

    [IS_LOADING_LOG]: state => {
      return state.logState.status.loading
    },

    [GET_LOG]: state => {
      return state.logState.data
    },

    [GET_LOG_LOAD_ERROR]: state => {
      return state.logState.status.error
    },

    [HAS_EMPTY_LOG_LOAD_RESULT]: state => {
      return state.logState.status.success && state.logState.data.length === 0
    },

    [IS_LOADING_HUNT_AREAS]: state => {
      return state.huntAreasState.status.loading
    },

    [GET_HUNT_AREAS]: state => {
      return state.huntAreasState.data
    },

    [GET_HUNT_AREAS_LOAD_ERROR]: state => {
      return state.huntAreasState.status.error
    },

    [HAS_EMPTY_HUNT_AREAS_LOAD_RESULT]: state => {
      return state.huntAreasState.status.success && state.huntAreasState.data.length === 0
    }
  },

  actions: {
    [LOAD_LOG_FOR_EMAIL]: async ({ commit, state }, { email }) => {
      if (!email) {
        return
      }

      if (email === state.logState.id) {
        // Log load has already started
        return
      }

      commit(SET_EMAIL, { email })
      commit(SET_LOG_LOAD_STARTED, { email })

      try {
        const response = await api.get(`admin/gamecams/${email}/log`)
        const loadResult = response.data

        if (!loadResult.success || !loadResult.log) {
          const error = loadResult.message || 'Unknown error'
          commit(SET_LOG_LOAD_FAILED, { email, error })
        } else {
          commit(SET_LOG_LOAD_FINISHED_SUCCESSFULLY, { email, log: loadResult.log })
        }
      } catch (error) {
        commit(SET_LOG_LOAD_FAILED, { email, error })
      }
    },

    [LOAD_HUNT_AREAS]: async ({ commit, state }) => {
      const email = state.email
      if (!email) {
        // No email
        return
      }

      if (email === state.huntAreasState.id) {
        // Hunt areas load has already started
        return
      }

      commit(SET_HUNT_AREAS_LOAD_STARTED, { email })

      try {
        const response = await api.get(`admin/gamecams/${email}/huntAreas`)

        const huntAreas = response.data
        commit(SET_HUNT_AREAS_LOAD_FINISHED_SUCCESSFULLY, { email, huntAreas })
      } catch (error) {
        commit(SET_HUNT_AREAS_LOAD_FAILED, { email, error })
      }
    },

    [RESET_LOG_STATE]: ({ commit }) => {
      commit(SET_EMAIL, { email: null })
      commit(RESET_LOG)
      commit(RESET_HUNT_AREAS)
    }
  }
}
